// ThreeFishVideo.js
// 基于 Three.js 的 Side-by-Side Shader 透明视频组件（专家方案）
import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

// 顶点着色器（专家报告 4.2.1）
const vertexShader = `
  varying vec2 vUv;
  
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// 片元着色器（专家报告 4.2.2）
const fragmentShader = `
  uniform sampler2D map;
  uniform float opacity;
  varying vec2 vUv;
  
  void main() {
    // 1. 计算左半部分（颜色）的 UV 坐标
    vec2 colorUV = vec2(vUv.x * 0.5, vUv.y);
    
    // 2. 计算右半部分（Alpha）的 UV 坐标
    vec2 alphaUV = vec2(vUv.x * 0.5 + 0.5, vUv.y);
    
    // 3. 采样纹理
    vec4 colorSample = texture2D(map, colorUV);
    vec4 alphaSample = texture2D(map, alphaUV);
    
    // 4. 获取 Alpha 值
    float computedAlpha = alphaSample.r;
    
    // 5. 边缘修正（专家建议）
    computedAlpha = smoothstep(0.05, 0.1, computedAlpha) * computedAlpha;
    
    // 6. 输出最终颜色
    gl_FragColor = vec4(colorSample.rgb, computedAlpha * opacity);
  }
`;

const ThreeFishVideo = ({ src, className, style }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const videoRef = useRef(null);
  const animationIdRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    console.log('🐠 初始化 Three.js Scene...');

    // 1. 创建 Video 元素（专家报告 4.2.3）
    const video = document.createElement('video');
    video.src = src;
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true; // iOS 必须静音
    video.playsInline = true; // iOS 必须属性
    videoRef.current = video;

    // 用户交互前置（专家报告 4.3.1）
    const playVideo = () => {
      video.play()
        .then(() => console.log('✅ Three.js 视频播放成功'))
        .catch(err => console.log('播放需要用户交互:', err));
    };
    
    // 尝试自动播放
    playVideo();
    
    // 如果自动播放失败，监听用户交互
    const handleUserInteraction = () => {
      playVideo();
      document.removeEventListener('click', handleUserInteraction);
    };
    document.addEventListener('click', handleUserInteraction);

    // 2. 创建 Three.js 场景
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // 3. 创建正交相机（填满屏幕）
    const aspect = container.clientWidth / container.clientHeight;
    const camera = new THREE.OrthographicCamera(-aspect, aspect, 1, -1, 0.1, 10);
    camera.position.z = 1;
    cameraRef.current = camera;

    // 4. 创建渲染器
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, // 启用透明背景
      antialias: true,
      powerPreference: 'high-performance' // 使用高性能 GPU
    });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setClearColor(0x000000, 0); // 透明背景
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // 5. 创建视频纹理（专家报告 4.2.3）
    const videoTexture = new THREE.VideoTexture(video);
    videoTexture.minFilter = THREE.LinearFilter;
    videoTexture.magFilter = THREE.LinearFilter;
    videoTexture.format = THREE.RGBAFormat;

    // 6. 创建自定义 Shader 材质（专家报告 4.2.3）
    const fishMaterial = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: videoTexture },
        opacity: { value: 1.0 }
      },
      vertexShader,
      fragmentShader,
      transparent: true, // 告知 Three.js 此物体透明
      side: THREE.DoubleSide
    });

    // 7. 创建平面网格（object-fit: cover 模式）
    // 确保视频总是填满屏幕，类似 CSS cover
    const screenAspect = container.clientWidth / container.clientHeight;
    // 视频实际宽高比（1080:1920 = 9:16 = 0.5625）
    const videoAspect = 9 / 16;
    
    let planeWidth, planeHeight;
    
    // cover 模式：选择较大的尺寸，确保填满
    if (screenAspect > videoAspect) {
      // 屏幕更宽（如电脑横屏），按宽度填满，高度可能裁切
      planeWidth = 2 * screenAspect;
      planeHeight = planeWidth / videoAspect;
    } else {
      // 屏幕更高（如手机竖屏），按高度填满，宽度可能裁切
      planeHeight = 2;
      planeWidth = planeHeight * videoAspect;
    }
    
    console.log('🐠 平面尺寸:', { planeWidth, planeHeight, screenAspect, videoAspect });
    
    const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
    const fishMesh = new THREE.Mesh(geometry, fishMaterial);
    scene.add(fishMesh);

    console.log('✅ Three.js 场景创建完成');

    // 8. 渲染循环（Three.js 自动管理）
    let frameCount = 0;
    let lastLogTime = Date.now();
    
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      
      frameCount++;
      const now = Date.now();
      if (now - lastLogTime > 2000) {
        const fps = Math.round(frameCount / ((now - lastLogTime) / 1000));
        console.log(`🐠 Three.js 渲染 - FPS: ${fps}, 视频: ${video.paused ? '暂停' : '播放'}`);
        frameCount = 0;
        lastLogTime = now;
      }
      
      renderer.render(scene, camera);
    };
    
    animate();

    // 处理窗口大小变化
    const handleResize = () => {
      if (!container) return;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const newAspect = width / height;
      
      camera.left = -newAspect;
      camera.right = newAspect;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      
      // 重新计算平面大小（cover 模式）
      const videoAspect = 9 / 16;
      let newPlaneWidth, newPlaneHeight;
      
      if (newAspect > videoAspect) {
        // 屏幕更宽，按宽度填满
        newPlaneWidth = 2 * newAspect;
        newPlaneHeight = newPlaneWidth / videoAspect;
      } else {
        // 屏幕更高，按高度填满
        newPlaneHeight = 2;
        newPlaneWidth = newPlaneHeight * videoAspect;
      }
      
      fishMesh.geometry.dispose();
      fishMesh.geometry = new THREE.PlaneGeometry(newPlaneWidth, newPlaneHeight);
      
      console.log('🐠 窗口调整:', { width, height, newPlaneWidth, newPlaneHeight });
    };
    window.addEventListener('resize', handleResize);

    // 清理函数
    return () => {
      console.log('🐠 清理 Three.js 场景');
      document.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('resize', handleResize);
      
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (video) {
        video.pause();
        video.src = '';
      }
      if (renderer) {
        renderer.dispose();
      }
      if (geometry) {
        geometry.dispose();
      }
      if (fishMaterial) {
        fishMaterial.dispose();
      }
      if (videoTexture) {
        videoTexture.dispose();
      }
      if (container && renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [src]);

  return (
    <div 
      ref={containerRef} 
      className={className}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        ...style
      }}
    />
  );
};

export default ThreeFishVideo;

